import React from 'react';
import { CalendarHeader } from './CalendarHeader';
import { DrawerMenu } from './DrawerMenu';
import { useAuth } from '@/components/auth/AuthProvider';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';

interface CalendarLayoutProps {
  children: React.ReactNode;
}

export const CalendarLayout: React.FC<CalendarLayoutProps> = ({ children }) => {
  const { user, isDemo, loading } = useAuth();

  // Show loading state while auth is initializing
  if (loading) {
    return (
      <div className="min-h-screen w-full bg-background">
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center p-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Loading...</h2>
            <p className="text-muted-foreground">
              Please wait while we prepare your experience.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Don't show header/drawer on auth page
  if (!user) {
    return <>{children}</>;
  }

  return (
    <ErrorBoundary resetKeys={[user?.id]}>
      <div className="min-h-screen min-h-[100dvh] w-full max-w-[100vw] overflow-x-hidden flex flex-col instructor-theme">
        {isDemo && (
          <div className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white text-center py-2 px-4 text-xs sm:text-sm font-medium" style={{paddingTop: 'max(8px, env(safe-area-inset-top, 0px))'}}>
            🚗 DEMO MODE - This is a preview version for ChatGPT
          </div>
        )}
        <DrawerMenu />
        <main className="relative z-1 flex-1 w-full max-w-[100vw] overflow-x-hidden overflow-y-auto">
          <ErrorBoundary
            resetKeys={[window.location.pathname]}
            fallback={
              <div className="min-h-screen flex items-center justify-center p-4">
                <div className="text-center p-8 max-w-md">
                  <h2 className="text-xl font-semibold mb-2">Page Error</h2>
                  <p className="text-muted-foreground mb-4">
                    This page encountered an error. Please try navigating to a different page.
                  </p>
                </div>
              </div>
            }
          >
            {children}
          </ErrorBoundary>
        </main>
      </div>
    </ErrorBoundary>
  );
};